import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Switch,
    Tooltip,
    Typography,
} from '@material-ui/core';

import { images } from 'assets';
import { default as userStore } from 'store/userStore';
import { theme } from 'config/theme';
import { useStyles } from '../styles';
import { useAppSelector } from 'index';
import { LocalStorageKeys } from 'constants/localStorageKeys';
import { IdName, RestaurantGroup } from 'api/models';
import i18n from 'i18n-js';
import clsx from 'clsx';
import restaurantDetailsStore from 'store/restaurantDetailsStore';

interface NavRestaurantSwitcherProps {
    isOpen: boolean;
    open: () => void;
}

export function NavRestaurantSwitcher(props: NavRestaurantSwitcherProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.filter((x) => x.id === user.currentRestaurantId)[0];
    const [isExpanded, setIsExpanded] = useState(false);
    const [isGroupView, setIsGroupView] = useState<boolean>(getStoredGroupView());
    const groups = useAppSelector(state => state.restaurantDetails.groups);
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(getStoredSelectedGroup());
    const selectedGroupName = groups.find((g) => g.id === selectedGroupId)?.name;
    const restaurants = user?.restaurants?.sort((a, b) => a.name.localeCompare(b.name));

    useEffect(() => {
        dispatch(userStore.actionCreators.getUserRestaurants());
        dispatch(restaurantDetailsStore.actionCreators.getMyGroups());
    }, []); // eslint-disable-line

    function handleExpand() {
        if (props.isOpen) {
            setIsExpanded(!isExpanded);
        } else {
            setIsExpanded(true);
            props.open();
        }
    }

    function changeRestaurant(id: string) {
        if (id !== user.currentRestaurantId) {
            dispatch(userStore.actionCreators.setUserCurrentRestaurant(id));
        }
    }

    function getStoredGroupView(): boolean {
        try {
            const stored = localStorage.getItem(LocalStorageKeys.navGroupView);
            return stored !== null ? JSON.parse(stored) : false;
        } catch {
            return false;
        }
    }

    function getStoredSelectedGroup(): string | null {
        try {
            const stored = localStorage.getItem(LocalStorageKeys.navSelectedGroup);
            return stored !== null ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }

    function handleToggleGroupView(e: React.ChangeEvent<HTMLInputElement>) {
        const next = e.target.checked;
        setIsGroupView(next);
        try {
            localStorage.setItem(LocalStorageKeys.navGroupView, JSON.stringify(next));
        } catch {}

        if (!next) {
            localStorage.removeItem(LocalStorageKeys.navSelectedGroup);
            setSelectedGroupId(null);
            setExpandedGroupId(null);
        }
    }

    function handleGroupExpand(groupId: string) {
        setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
    }

    function handleSelectGroupRestaurant(group: RestaurantGroup, restaurant: IdName) {
        localStorage.setItem(LocalStorageKeys.navSelectedGroup, JSON.stringify(group.id));
        setSelectedGroupId((prev) => (prev !== group.id ? group.id : prev));
        if (currentRestaurant?.id !== restaurant.id) {
            changeRestaurant(restaurant.id);
        }
    }

    return (
        <>
            <Box className={classes.root}>
                <Accordion expanded={isExpanded && props.isOpen}>
                    <AccordionSummary
                        className={clsx(
                            props.isOpen ? classes.header : classes.headerHidden,
                            classes.textEllipsisContainer
                        )}
                        expandIcon={<ExpandIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        onClick={handleExpand}
                    >
                        {!isGroupView && (
                            <Tooltip title={currentRestaurant?.name ?? ''} placement="top">
                                <Typography className={classes.textEllipsis}>
                                    {currentRestaurant?.name}
                                </Typography>
                            </Tooltip>
                        )}

                        {isGroupView && selectedGroupId === null && (
                            <Typography className={classes.textEllipsis}>
                                {isExpanded
                                    ? i18n.t('common.selectGroup')
                                    : currentRestaurant?.name}
                            </Typography>
                        )}

                        {isGroupView && selectedGroupId !== null && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                className={classes.textEllipsisContainer}
                            >
                                <Tooltip title={currentRestaurant?.name ?? ''} placement="top">
                                    <Typography variant="body1" className={classes.textEllipsis}>
                                        {currentRestaurant?.name}
                                    </Typography>
                                </Tooltip>
                                <Tooltip title={selectedGroupName ?? ''} placement="top">
                                    <Typography variant="body2" className={classes.textEllipsis}>
                                        {selectedGroupName}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        )}
                    </AccordionSummary>
                    <AccordionDetails className={classes.flexColumn}>
                        <List>
                            {groups.length > 0 && (
                                <ListItem divider>
                                    <Box className={classes.toggleRow}>
                                        <FormControlLabel
                                            className={clsx(
                                                classes.flexRowSpaceBetween,
                                                classes.mVertical5,
                                                classes.toggleControl
                                            )}
                                            control={
                                                <Switch
                                                    checked={isGroupView}
                                                    onChange={handleToggleGroupView}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography className={classes.fontWeight500}>
                                                    {i18n.t('common.viewByGroups')}
                                                </Typography>
                                            }
                                            labelPlacement="start"
                                        />
                                    </Box>
                                </ListItem>
                            )}

                            {!isGroupView &&
                                restaurants?.map((restaurant) => (
                                    <ListItem
                                        divider
                                        key={restaurant.id}
                                        selected={user.currentRestaurantId === restaurant.id}
                                        onClick={() => changeRestaurant(restaurant.id)}
                                        className={classes.textEllipsisContainer}
                                        style={{
                                            cursor:
                                                user.currentRestaurantId === restaurant.id
                                                    ? 'unset'
                                                    : 'pointer',
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    className={classes.textEllipsis}
                                                    style={{
                                                        color: theme.palette.text.disabled,
                                                        fontWeight: 400,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {restaurant.addressLine}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    className={classes.textEllipsis}
                                                    style={{
                                                        color: theme.palette.primary.dark,
                                                        fontWeight: 400,
                                                        fontSize: 16,
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={restaurant?.name ?? ''}
                                                        placement="top"
                                                        enterDelay={1000}
                                                        enterNextDelay={1000}
                                                    >
                                                        <span>{restaurant.name}</span>
                                                    </Tooltip>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}

                            {isGroupView &&
                                groups.map((group) => (
                                    <Accordion
                                        key={group.id}
                                        expanded={expandedGroupId === group.id}
                                        onChange={() => handleGroupExpand(group.id)}
                                        className={classes.groupAccordion}
                                    >
                                        <AccordionSummary expandIcon={<ExpandIcon />}>
                                            <Typography
                                                className={clsx(
                                                    classes.groupName,
                                                    classes.textEllipsis3
                                                )}
                                            >
                                                <Tooltip
                                                    title={group.name ?? ''}
                                                    placement="top"
                                                    enterDelay={1000}
                                                    enterNextDelay={1000}
                                                >
                                                    <span>{group.name}</span>
                                                </Tooltip>
                                            </Typography>
                                        </AccordionSummary>

                                        <AccordionDetails style={{ padding: 0 }}>
                                            <List disablePadding>
                                                {group.restaurants.map((restaurant) => (
                                                    <ListItem
                                                        divider
                                                        key={`${group.id}_${restaurant.id}`}
                                                        selected={
                                                            user.currentRestaurantId ===
                                                                restaurant.id &&
                                                            selectedGroupId === group.id
                                                        }
                                                        onClick={() =>
                                                            handleSelectGroupRestaurant(
                                                                group,
                                                                restaurant
                                                            )
                                                        }
                                                        className={classes.restaurantItem}
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <ListItemText
                                                            secondary={
                                                                <Typography
                                                                    className={classes.textEllipsis}
                                                                    style={{
                                                                        color: theme.palette.primary
                                                                            .dark,
                                                                        fontWeight: 400,
                                                                        fontSize: 16,
                                                                    }}
                                                                >
                                                                    <Tooltip
                                                                        title={
                                                                            restaurant?.name ?? ''
                                                                        }
                                                                        placement="top"
                                                                        enterDelay={1000}
                                                                        enterNextDelay={1000}
                                                                    >
                                                                        <span>
                                                                            {restaurant.name}
                                                                        </span>
                                                                    </Tooltip>
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </>
    );
}

function ExpandIcon() {
    return <img src={images.icons.triangleDown} alt="triangle-down" />;
}
